import { InputBase, SxProps, Theme, Typography, useTheme } from '@mui/material'
import { ChangeEvent, ReactNode, RefObject, useEffect, useState } from 'react'

export interface BaseTextInputProps {
  placeholder?: string
  prefix?: ReactNode | string
  suffix?: ReactNode | string
  inputRef?: RefObject<HTMLInputElement | null>
  value: string
  error?: string
  onChange: (value: string) => void
  size?: 'small' | 'medium' | 'large'
  autoFocus?: boolean
  uppercase?: boolean
  type?: 'text' | 'phoneNumber'
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  disabled?: boolean
  onFocus?: () => void
  onBlur?: () => void
  /** When true, trims leading/trailing whitespace on blur and syncs via `onChange` if it changed. */
  trimOnBlur?: boolean
  /** Rewrites each keystroke before it is displayed or reported, e.g. to cap length or add separators. */
  format?: (value: string) => string
  setSelection?: (selection: { start: number, end: number }) => void
  autoComplete?: string
  multiline?: boolean
  sx?: SxProps<Theme>
  minRows?: number
  maxRows?: number
}

const getFontSize = (size: BaseTextInputProps['size'], theme: Theme) => {
  if (size === 'small') return theme.typography.body2.fontSize
  if (size === 'large') return theme.typography.h5.fontSize
  return theme.typography.body1.fontSize
}

export default function TextInput(props: BaseTextInputProps) {
  const {
    placeholder, prefix, suffix, inputRef, value, error, onChange,
    size = 'medium', autoFocus, onKeyDown, disabled, onFocus, onBlur, trimOnBlur,
    setSelection, type = 'text', autoComplete,
    multiline = false, sx, minRows, maxRows,
  } = props
  const theme = useTheme()
  const [isFocused, setIsFocused] = useState(false)
  const [internalValue, setInternalValue] = useState(value)

  useEffect(() => {
    if (!isFocused) {
      setInternalValue(value)
    }
  }, [value, isFocused])

  function validatePhoneNumber(val: string) {
    if (type !== 'phoneNumber') return true
    // Basic phone number validation: allows any number including + signs, spaces, and dashes
    const phoneRegex = /^[\d\s\-+]+$/
    return phoneRegex.test(val) || val === ''
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    let newValue = event.target.value
    if (props.uppercase) {
      newValue = newValue.toUpperCase()
    }
    setSelection?.({ start: event.target.selectionStart || 0, end: event.target.selectionEnd || 0 })
    if (type === 'phoneNumber' && !validatePhoneNumber(newValue)) return
    if (props.format) {
      newValue = props.format(newValue)
    }
    setInternalValue(newValue)
    onChange(newValue)
  }

  function handleFocus() {
    setIsFocused(true)
    onFocus?.()
  }

  function handleBlur() {
    if (trimOnBlur && typeof internalValue === 'string') {
      const trimmed = internalValue.trim()
      if (trimmed !== internalValue) {
        setInternalValue(trimmed)
        onChange(trimmed)
      }
    }
    setIsFocused(false)
    onBlur?.()
  }

  const handleInputClick = () => {
    if (inputRef?.current) {
      setSelection?.({ start: inputRef.current.selectionStart || 0, end: inputRef.current.selectionEnd || 0 })
    }
  }

  const fontSize = getFontSize(size, theme)

  return <InputBase
    disabled={disabled}
    error={!!error}
    onChange={handleChange}
    onFocus={handleFocus}
    onBlur={handleBlur}
    onKeyDown={onKeyDown}
    onClick={handleInputClick}
    onKeyUp={handleInputClick}
    inputRef={inputRef}
    value={internalValue}
    placeholder={placeholder}
    size={size === 'large' ? 'medium' : size}
    autoComplete={autoComplete}
    autoFocus={autoFocus}
    multiline={multiline}
    minRows={minRows}
    maxRows={maxRows}
    sx={{
      flexGrow: 1,
      '&.MuiInputBase-root': {
        borderColor: error ? 'error.main' : 'secondary.dark',
      },
      '& input': {
        py: size === 'small' ? 1.5 : 2,
        px: 2,
        fontSize,
        fontWeight: size === 'large' ? 500 : 400,
        caretColor: theme.palette.primary.main,
      },
      ...sx,
    }}
    startAdornment={typeof prefix === 'string'
      ? <Typography
        sx={{
          color: 'text.secondary',
          fontSize,
          ml: 2,
          mr: -1,
        }}
      >
        {prefix}
      </Typography>
      : prefix
    }
    endAdornment={typeof suffix === 'string'
      ? <Typography
        color='textSecondary'
        sx={{
          color: 'text.secondary',
          fontSize,
          mr: 2,
          position: 'absolute',
          ml: 3,
          left: 0,
          pointerEvents: 'none',
        }}
      >
        {/* This is hidden text that is used to calculate the offset of the suffix */}
        <span style={{ opacity: 0 }}>{internalValue}</span>
        {suffix}
      </Typography>
      : suffix
    }
  />
}